import { Button, Divider, Modal } from 'antd';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import i18n from 'i18n';
import { useCallback, useEffect, useState } from 'react';
import LOCALS from 'commons/locals';
import {
  createProductFormSchema,
  CreateProductFormSchemaType,
  generateInitSelectedRankDescOptions,
} from './common';
import ProductCategory from './form-items/category';
import ProductBox from './form-items/box';
import ProductExtendType from './form-items/extend-type';
import ProductStockPlace from './form-items/stock-place';
import ProductBrand from './form-items/brand';
import ProductPrice from './form-items/price';
import ProductCostPrice from './form-items/cost-price';
import ProductCareCost from './form-items/care-cost';
import ProductReferencePrice from './form-items/reference-price';
import ProductSkuType from './form-items/sku-type';
import { PRODUCT_STOCK_TYPE } from 'commons/options';
import ProductStock from './form-items/stock';
import ProductSourceType from './form-items/source-type';
import ProductPromotionType from './form-items/promotion-type';
import ProductOriginalPrice from './form-items/original-price';
import ProductRemark from './form-items/remark';
import ProductDescription from './form-items/description';
import ProductRank from './form-items/rank';
import ProductColors from './form-items/colors';
import ProductColor from './form-items/color';
import ProductColorRemark from './form-items/color-remark';
import ProductMaterialRemark from './form-items/material-remark';
import ProductMaterial from './form-items/material';
import ProductHardwareRemark from './form-items/hardware-remark';
import ProductHardware from './form-items/hardware';
import ProductStamp from './form-items/stamp';
import ProductStampRemark from './form-items/stamp-remark';
import ProductFullStamp from './form-items/full-stamp';
import ProductAccessory from './form-items/accessory';
import ProductCollections from './form-items/collections';
import ProductFacebookEnabled from './form-items/facebook-enabled';
import ProductAlbumPics from './form-items/album-pics';
import ProductName from './form-items/name';
import ProductSize from './form-items/size';
import ProductSubTitle from './form-items/sub-title';
import ProductNote from './form-items/note';
import ProductDetailTitle from './form-items/detail-title';
import ProductRankDesc from './form-items/rank-desc';
import { useToggle } from 'react-use';
import { CheckCircleOutlined } from '@ant-design/icons';
import {
  CreateProductDtoType,
  createProductNew,
  UpdateProductDtoType,
  updateProductNew,
} from 'apis/pms';
import { last } from 'lodash-es';
import { UpdateProductFormInitData } from '../update-page-v2/types';
import InternalImages from './form-items/internal-images';
import { PROMOTION_TYPE_MAP } from 'constant/pms-product';
import { PRODUCT_SOURCE_TYPE_MAP } from 'constant/pms-product';

type ProductCreateFormProps = {
  productId?: number;
  initData?: UpdateProductFormInitData;
  disabledCostPrice: boolean;
  disabledCareCost: boolean;
  minCostPrice: number;
  canEditSourceType: boolean;
  viewMode: boolean;
};

const ProductCreateForm = ({
  initData,
  productId,
  disabledCostPrice,
  disabledCareCost,
  minCostPrice,
  canEditSourceType,
  viewMode,
}: ProductCreateFormProps) => {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, errors },
    reset,
  } = useForm<CreateProductFormSchemaType>({
    resolver: zodResolver(createProductFormSchema),
    disabled: viewMode,
    defaultValues: {
      productCategoryIdList: [],
      boxId: null,
      brandName: 'Hermes',
      skuType: PRODUCT_STOCK_TYPE.SINGLE,
      referencePrice: null,
      originalPrice: null,
      stock: 1,
      sourceType: PRODUCT_SOURCE_TYPE_MAP.RECYCLE,
      promotionType: PROMOTION_TYPE_MAP.NORMAL,
      facebookEnabled: 0,
      taxRegime: null,
      albumPics: [
        // 'https://img.ginzaxiaoma.com/images/20250918/af303b659f616204ed29594784e49148.jpg',
        // 'https://img.ginzaxiaoma.com/images/20250918/45b9a3c6ff2b78dca32d41b91f94e680.jpg',
        // 'https://img.ginzaxiaoma.com/images/20250918/1537d53755057ecf27007337645f702d.jpg',
        // 'https://img.ginzaxiaoma.com/images/20250918/e85d2ffe50686e6cd11bffecc7c98f1e.jpg',
        // 'https://img.ginzaxiaoma.com/images/20250918/33f3d582b32a95d6b4d8132341719519.jpg',
        // 'https://img.ginzaxiaoma.com/images/20250918/afee973a48a8023c23b8063e35d9a03a.jpg',
      ],
      internalImages: [],
      name: {
        en: '',
        ja: '',
        zh_CN: '',
        zh_TW: '',
      },
      size: {
        en: '',
        ja: '',
        zh_CN: '',
        zh_TW: '',
      },
      subTitle: {
        en: '',
        ja: '',
        zh_CN: '',
        zh_TW: '',
      },
      note: {
        en: '',
        ja: '',
        zh_CN: '',
        zh_TW: '',
      },
      detailTitle: {
        en: '',
        ja: '',
        zh_CN: '',
        zh_TW: '',
      },
      selectedRankDescOptions: generateInitSelectedRankDescOptions(),
    },
  });

  useEffect(() => {
    if (initData) {
      reset({
        ...initData,
        selectedRankDescOptions: generateInitSelectedRankDescOptions(),
      });
    }
  }, [initData, reset]);

  const [isVisible, toggleIsVisible] = useToggle(false);
  const [isLoading, toggleIsLoading] = useToggle(false);
  const [isSuccessData, setIsSuccessData] = useState<{
    id: number;
  } | null>(null);

  const onBack = useCallback(() => {
    if (productId) {
      if (!document.referrer) {
        window.location.href = '/pms/product-list';
      } else {
        window.history.go(-1);
      }
    } else {
      window.location.href = `/pms/product-list?pageNum=1&pageSize=10&keyword=${isSuccessData?.id}`;
    }
  }, [isSuccessData?.id, productId]);

  const [submitData, setSubmitData] =
    useState<CreateProductFormSchemaType | null>(null);
  const [albumPicsUploading, setAlbumPicsUploading] = useState(false);
  const [internalImagesUploading, setInternalImagesUploading] = useState(false);
  const imageUploading = albumPicsUploading || internalImagesUploading;
  const onSubmit = useCallback(
    (data: CreateProductFormSchemaType) => {
      if (viewMode || imageUploading) {
        return;
      }
      toggleIsVisible();
      setSubmitData(data);
    },
    [imageUploading, toggleIsVisible, viewMode],
  );

  useEffect(() => {
    if (isDirty && !isSuccessData) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        const confirmationMessage = i18n.t(
          'changes_have_not_been_saved',
        ) as string;
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isDirty, isSuccessData]);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          <div className="grid grid-cols-2">
            <ProductCategory
              control={control}
              errors={errors}
              setValue={setValue}
            />
            <ProductExtendType control={control} />
          </div>
          <div className="grid grid-cols-2">
            <ProductBox control={control} setValue={setValue} />
          </div>

          <div className="grid grid-cols-1">
            <ProductName
              control={control}
              errors={errors}
              setValue={setValue}
            />
          </div>

          <div className="grid grid-cols-2">
            <ProductStockPlace
              control={control}
              errors={errors}
              setValue={setValue}
            />
            <ProductBrand
              control={control}
              errors={errors}
              setValue={setValue}
            />
          </div>

          <div className="grid grid-cols-2">
            <ProductPrice
              control={control}
              errors={errors}
              setValue={setValue}
            />
            <ProductCostPrice
              control={control}
              errors={errors}
              disabled={disabledCostPrice || viewMode}
              minCostPrice={minCostPrice}
            />
          </div>

          <div className="grid grid-cols-2">
            <ProductReferencePrice control={control} />
            <ProductSourceType
              control={control}
              canEditSourceType={canEditSourceType && !viewMode}
            />
          </div>

          {productId && (
            <div className="grid grid-cols-2">
              <ProductCareCost
                control={control}
                errors={errors}
                disabled={disabledCareCost || viewMode}
              />
            </div>
          )}

          <div className="grid grid-cols-2">
            <ProductSkuType control={control} productId={productId} />
            <ProductStock
              control={control}
              errors={errors}
              productId={productId}
            />
          </div>

          <div className="grid grid-cols-2">
            <ProductPromotionType control={control} />
            <ProductOriginalPrice control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductDescription control={control} productId={productId} />
            <ProductRemark control={control} />
          </div>

          <Divider />

          <div className="grid grid-cols-2">
            <ProductRank
              control={control}
              errors={errors}
              setValue={setValue}
            />
          </div>

          <div className="grid grid-cols-1">
            <ProductRankDesc
              control={control}
              setValue={setValue}
              productId={productId}
              initRankDesc={initData?.rankDesc?.en}
            />
          </div>

          <div className="grid grid-cols-2">
            <ProductColors control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductColor control={control} setValue={setValue} />
            <ProductColorRemark control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductMaterial control={control} setValue={setValue} />
            <ProductMaterialRemark control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductHardware control={control} setValue={setValue} />
            <ProductHardwareRemark control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductStamp control={control} setValue={setValue} />
            <ProductStampRemark control={control} />
          </div>

          <div className="grid grid-cols-2">
            <ProductFullStamp control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductAccessory control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductSize control={control} />
          </div>

          <Divider />

          <div className="grid grid-cols-1">
            <ProductSubTitle control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductNote control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductDetailTitle control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductCollections control={control} setValue={setValue} />
          </div>

          <div className="grid grid-cols-2">
            <ProductFacebookEnabled control={control} />
          </div>

          <div className="grid grid-cols-1">
            <ProductAlbumPics
              control={control}
              productId={productId}
              disabled={viewMode}
              onUploadingChange={setAlbumPicsUploading}
            />
          </div>

          <div className="grid grid-cols-1">
            <InternalImages
              control={control}
              productId={productId}
              disabled={viewMode}
              onUploadingChange={setInternalImagesUploading}
            />
          </div>

          {!viewMode && (
            <div className="flex justify-center">
              <Button
                htmlType="submit"
                type="primary"
                disabled={imageUploading}
              >
                {i18n.t(LOCALS.submit)}
              </Button>
            </div>
          )}
        </div>
      </form>
      <Modal
        title={i18n.t(LOCALS.confirm_submit)}
        open={isVisible}
        onCancel={isSuccessData ? onBack : toggleIsVisible}
        footer={
          <div className="flex gap-2 justify-end">
            {isSuccessData ? (
              <>
                <Button onClick={onBack} type="primary">
                  {i18n.t(LOCALS.back)}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={toggleIsVisible}>
                  {i18n.t(LOCALS.cancel)}
                </Button>
                <Button
                  type="primary"
                  onClick={async () => {
                    if (!submitData) return;
                    toggleIsLoading();

                    const {
                      owner,
                      productCategoryIdList,
                      boxId,
                      brandName,
                      currency,
                      sourceType,
                      promotionType,
                      rank,
                      facebookEnabled,
                      price,
                      costPrice,
                      careCost,
                      skuType,
                      stock,
                      extendType,
                      referencePrice,
                      originalPrice,
                      description,
                      remark,
                      colors,
                      color,
                      colorRemark,
                      material,
                      materialRemark,
                      hardware,
                      hardwareRemark,
                      stamp,
                      stampRemark,
                      fullStamp,
                      accessory,
                      collections,
                      albumPics,
                      name,
                      size,
                      subTitle,
                      note,
                      detailTitle,
                      stockPlace,
                      rankDesc,
                      priceHkd,
                      taxRegime,
                      internalImages,
                    } = submitData;
                    const productCategoryId =
                      productCategoryIdList[productCategoryIdList.length - 1];

                    const i18nList: CreateProductDtoType['i18nList'] = [];
                    i18nList.push({
                      lang: 'en',
                      name: name.en,
                      rankDesc: rankDesc.en,
                      size: size.en,
                      subTitle: subTitle.en,
                      note: note.en,
                      detailTitle: detailTitle.en,
                    });
                    i18nList.push({
                      lang: 'ja',
                      name: name.ja,
                      rankDesc: rankDesc.ja,
                      size: size.ja,
                      subTitle: subTitle.ja,
                      note: note.ja,
                      detailTitle: detailTitle.ja,
                    });
                    i18nList.push({
                      lang: 'zh_CN',
                      name: name.zh_CN,
                      rankDesc: rankDesc.zh_CN,
                      size: size.zh_CN,
                      subTitle: subTitle.zh_CN,
                      note: note.zh_CN,
                      detailTitle: detailTitle.zh_CN,
                    });
                    i18nList.push({
                      lang: 'zh_TW',
                      name: name.zh_TW,
                      rankDesc: rankDesc.zh_TW,
                      size: size.zh_TW,
                      subTitle: subTitle.zh_TW,
                      note: note.zh_TW,
                      detailTitle: detailTitle.zh_TW,
                    });

                    const requestData: UpdateProductDtoType = {
                      owner,
                      productCategoryId,
                      boxId: boxId ?? null,
                      extendType,
                      brandName,
                      currency,
                      price,
                      costPrice,
                      careCost,
                      referencePrice,
                      stockPlace,
                      sourceType,
                      promotionType,
                      originalPrice,
                      description,
                      remark,
                      rank,
                      colors,
                      color,
                      colorRemark,
                      material: material
                        ?.map((item) => {
                          return last(item) || '';
                        })
                        .filter(Boolean),
                      materialRemark,
                      hardware,
                      hardwareRemark,
                      stamp,
                      stampRemark,
                      fullStamp,
                      accessory,
                      collections,
                      facebookEnabled,
                      albumPics,
                      i18nList,
                      priceHkd,
                      taxRegime,
                      internalImages,
                    };

                    if (productId) {
                      await updateProductNew({
                        id: productId,
                        data: requestData,
                      });

                      setIsSuccessData({ id: productId });
                    } else {
                      const res = await createProductNew({
                        ...requestData,
                        stock,
                        skuType,
                      });
                      setIsSuccessData(res);
                    }
                    toggleIsLoading();
                  }}
                  loading={isLoading}
                >
                  Ok
                </Button>
              </>
            )}
          </div>
        }
      >
        {isSuccessData && (
          <div className="space-y-4">
            <div className="text-green-500 flex items-center justify-center">
              <CheckCircleOutlined style={{ fontSize: 64 }} />
            </div>

            <div className="text-center flex items-center justify-center gap-2">
              <span>{i18n.t(LOCALS.sales_product_id)}:</span>
              <span>{isSuccessData.id}</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductCreateForm;
